import axios from 'axios'
import ls from 'local-storage'
import configuration from 'src/configuration'

/**
 * Factory per creare un gestore di JWT delegato per un servizio specifico
 * @param {string} audience - L'audience del servizio (es. 'cash-planning', 'reverse-factoring-handler')
 * @param {string} storagePrefix - Prefisso per le chiavi del localStorage
 * @returns {object} Oggetto con funzioni per gestire il JWT delegato
 */
export const createDelegateJWTManager = (audience, storagePrefix) => {
  // Chiavi per il localStorage
  const STORAGE_KEYS = {
    DELEGATE_JWT: `${storagePrefix}_delegate_jwt`,
    JWT_EXPIRES_AT: `${storagePrefix}_jwt_expires_at`
  }

  // Crea un'istanza axios per le chiamate al servizio principale
  const mainApiAxios = axios.create({
    baseURL: configuration.apiBaseUrl
  })

  // Promise caching per evitare multiple richieste simultanee
  let jwtPromise = null

  // Funzione per ottenere il JWT delegato dal servizio principale
  const getDelegateJWT = async () => {
    // Se c'è già una richiesta in corso, restituisci quella promise
    if (jwtPromise) {
      return await jwtPromise
    }

    // Crea una nuova promise per ottenere il JWT
    jwtPromise = (async () => {
      try {
        const mainToken = ls.get('plsadv_token')
        if (!mainToken) {
          throw new Error('No main authentication token available')
        }

        const response = await mainApiAxios.post('/users/self/auth-delegate', 
          { audience },
          { 
            headers: {
              'Authorization': mainToken,
              'Content-Type': 'application/json'
            }
          }
        )

        const jwt = response.data.token || response.data.jwt || response.data
        const expiresAt = Date.now() + (9 * 60 * 1000) // 9 minuti (buffer di 1 minuto)
        
        // Salva il JWT e la sua scadenza
        ls.set(STORAGE_KEYS.DELEGATE_JWT, jwt)
        ls.set(STORAGE_KEYS.JWT_EXPIRES_AT, expiresAt)
        
        return jwt
      } catch (error) {
        console.error(`Error obtaining delegate JWT for ${audience}:`, error)
        throw error
      } finally {
        // Pulisci la promise cache una volta completata (successo o errore)
        jwtPromise = null
      }
    })()

    return await jwtPromise
  }

  // Funzione per controllare se il JWT delegato è valido
  const isJWTValid = () => {
    const jwt = ls.get(STORAGE_KEYS.DELEGATE_JWT)
    const expiresAt = ls.get(STORAGE_KEYS.JWT_EXPIRES_AT)
    
    if (!jwt || !expiresAt) {
      return false
    }
    
    return Date.now() < expiresAt
  }

  // Funzione per ottenere un JWT delegato valido (rinnova se necessario)
  const getValidDelegateJWT = async () => {
    if (isJWTValid()) {
      return ls.get(STORAGE_KEYS.DELEGATE_JWT)
    }
    
    // JWT scaduto o non presente, ottienine uno nuovo
    return await getDelegateJWT()
  }

  // Funzione per pulire il JWT delegato (da chiamare al logout)
  const clearDelegateJWT = () => {
    ls.remove(STORAGE_KEYS.DELEGATE_JWT)
    ls.remove(STORAGE_KEYS.JWT_EXPIRES_AT)
    // Pulisci anche la promise cache
    jwtPromise = null
  }

  // Funzione per forzare il refresh del JWT delegato
  const refreshDelegateJWT = async () => {
    clearDelegateJWT()
    return await getDelegateJWT()
  }

  // Funzione per ottenere informazioni dal JWT delegato corrente
  const getDelegateJWTInfo = () => {
    const jwt = ls.get(STORAGE_KEYS.DELEGATE_JWT)
    const expiresAt = ls.get(STORAGE_KEYS.JWT_EXPIRES_AT)
    
    if (!jwt) {
      return null
    }
    
    try {
      // Decodifica il payload del JWT (senza verifica della firma, solo per info)
      const payload = JSON.parse(atob(jwt.split('.')[1]))
      return {
        jwt,
        payload,
        expiresAt: new Date(expiresAt),
        isValid: isJWTValid()
      }
    } catch (error) {
      console.error('Error decoding JWT:', error)
      return {
        jwt,
        expiresAt: new Date(expiresAt),
        isValid: isJWTValid()
      }
    }
  }

  // Funzione per ottenere manualmente un JWT delegato valido (per uso esterno)
  const ensureDelegateJWT = async () => {
    try {
      return await getValidDelegateJWT()
    } catch (error) {
      console.error(`Error ensuring delegate JWT for ${audience}:`, error)
      throw error
    }
  }

  // Restituisci l'oggetto con tutte le funzioni pubbliche
  return {
    getValidDelegateJWT,
    clearDelegateJWT,
    refreshDelegateJWT,
    getDelegateJWTInfo,
    ensureDelegateJWT,
    isJWTValid
  }
}

/**
 * Crea interceptor request per axios che aggiunge automaticamente il JWT delegato
 * @param {object} jwtManager - Il manager JWT creato con createDelegateJWTManager
 * @returns {function} Interceptor request per axios
 */
export const createAuthRequestInterceptor = (jwtManager) => {
  return async (config) => {
    try {
      // Ottieni un JWT delegato valido
      const delegateJWT = await jwtManager.getValidDelegateJWT()

      if (delegateJWT) {
        // Il delegateJWT è già il token stesso
        const token = delegateJWT?.data?.token || delegateJWT
        config.headers.Authorization = `Bearer ${token}`
      }
    } catch (error) {
      console.error('Error getting delegate JWT for request:', error)
      // Fallback al token principale se il JWT delegato fallisce
      const mainToken = ls.get('plsadv_token')
      if (mainToken) {
        config.headers.Authorization = mainToken
      }
    }
    return config
  }
}

/**
 * Crea interceptor response per axios che gestisce errori di autenticazione
 * @param {object} jwtManager - Il manager JWT creato con createDelegateJWTManager
 * @param {function} errorCallback - Callback opzionale per gestire errori aggiuntivi
 * @returns {object} Oggetto con onSuccess e onError per axios interceptor
 */
export const createAuthResponseInterceptor = (jwtManager, errorCallback = null) => {
  return {
    onSuccess: (response) => response,
    onError: (error) => {
      if (errorCallback) {
        errorCallback(error)
      }
      
      // Se riceviamo un errore 401/403, potrebbe essere che il JWT delegato sia scaduto o non valido
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        console.warn('Authentication error, clearing delegate JWT')
        jwtManager.clearDelegateJWT()
      }
      
      return Promise.reject(error)
    }
  }
}

